<template>
    <div class="form-card">
        <div v-if="isContentReady" class="h-full">
            <div class="my-4">
                <img class="mb-3" src="@/assets/logo.png" alt="logo" />
                <div class="font-normal text-lg uppercase text-center">
                    Автоматизированная система управления сбытом энергии
                </div>
            </div>
            <dx-popup
                :visible="isAlertVisible"
                :show-close-button="true"
                width="500px"
                height="160px"
                title="Сгенерированный пароль"
                @hidden="onHidden"
            >
                <div class="h-full flex flex-col justify-between items-center">
                    <div class="text-center text-[14px]">
                        Используйте сгенерированный пароль или придумайте свой:<br />{{ generatedPassword }}
                    </div>
                    <div class="w-[80%] flex justify-around">
                        <dx-button class="w-[45%]" text="Отмена" @click="close" />
                        <dx-button class="w-[45%]" text="Использовать" @click="usePassword" />
                    </div>
                </div>
            </dx-popup>
            <div class="h-[500px] flex flex-col justify-between">
                <div>
                    <dx-text-box v-bind="newPasswordProps" v-model:value="passwords.new.password" />
                    <div class="mt-3">
                        <div>
                            <label class="text-xs font-bold">{{ labelPassword }}</label>
                            <ul class="mt-1 ml-5 py-2 text-xs list-disc">
                                <li v-for="(rule, index) in passwordRules.base" :key="index">{{ rule }}</li>
                            </ul>
                        </div>
                        <div v-if="isFirstTry">
                            <label class="mt-4 text-xs font-bold">Доп. требования:</label>
                            <ul class="mt-1 ml-5 py-2 text-xs list-disc">
                                <li v-for="(rule, index) in passwordRules.optional" :key="index">{{ rule }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div>
                    <dx-text-box
                        class="mb-3"
                        v-bind="newRepeatPasswordProps"
                        v-model:value="passwords.newRepeat.password"
                    />
                    <dx-button v-bind="changePasswordProps" />
                </div>
            </div>
        </div>
        <dx-loader class="absolute top-1/2 left-[45%]" v-if="!isContentReady" />
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { DxTextBox, DxButton, DxPopup } from 'devextreme-vue';
import DxLoader from '@/components/loader/dx-loader.vue';
import { dxUiAlert } from '@/utils/dx-helper';

import {
    getPasswordValidateRulesCommon,
    putPasswordRestoreLinkCommon,
    checkRestoreLinkCommon,
    getGeneratedPasswordCommon,
} from '@/service/admin-service';

import { useRouter } from 'vue-router';
const router = useRouter();

import usersLocalStore from '@/views/administration/users/store';
const { state } = usersLocalStore;

const passwords = reactive({
    new: {
        password: '',
        display: false,
        valid: true,
    },
    newRepeat: {
        password: '',
        display: false,
    },
});

const labelPassword = ref('Пароль Вашей учетной записи должен соответствовать следующим требованиям:');

const passwordRules = reactive({
    base: [],
    optional: [],
});

const isContentReady = ref(false);
const isFirstTry = ref(true);
const isAlertVisible = ref(false);
const generatedPassword = ref(null);

let url = null;

const newPasswordProps = computed(() => ({
    placeholder: 'Введите новый пароль',
    mode: passwords.new.display ? 'text' : 'password',
    validationStatus: passwords.new.valid ? 'valid' : 'invalid',
    valueChangeEvent: 'keyup',
    buttons: [
        {
            location: 'after',
            name: 'showGeneratedPassword',
            options: {
                icon: 'description',
                onClick: () => showGeneratedPassword(),
            },
        },
        {
            location: 'after',
            name: 'changePasswordVisibilityButton',
            options: {
                icon: passwords.new.display ? 'eyeopen' : 'eyeclose',
                onClick: () => (passwords.new.display = !passwords.new.display),
            },
        },
    ],
}));

const newRepeatPasswordProps = computed(() => ({
    placeholder: 'Повторите новый пароль',
    mode: passwords.newRepeat.display ? 'text' : 'password',
    valueChangeEvent: 'keyup',
    buttons: [
        {
            location: 'after',
            name: 'changePasswordVisibilityButton',
            options: {
                icon: passwords.newRepeat.display ? 'eyeopen' : 'eyeclose',
                onClick: () => (passwords.newRepeat.display = !passwords.newRepeat.display),
            },
        },
    ],
}));

const showGeneratedPassword = () => {
    isAlertVisible.value = true;
};

const usePassword = () => {
    passwords.new.password = generatedPassword.value;
    isAlertVisible.value = false;
};

const close = () => {
    isAlertVisible.value = false;
};

const onHidden = () => {
    isAlertVisible.value = false;
};

const changePasswordProps = computed(() => ({
    text: 'Сменить пароль',
    disabled: passwords.new.password !== passwords.newRepeat.password || passwords.new.password === '',
    width: '100%',
    type: 'default',
    onClick: changePassword,
}));

onMounted(async () => {
    url = router.currentRoute.value.fullPath;
    const resultRestoreLink = await checkRestoreLinkCommon(url);
    if (resultRestoreLink?.success) {
        state.userRequestBody = resultRestoreLink.data;
        const result = await getGeneratedPasswordCommon(state.userRequestBody.user_type_id);
        if (result?.success) {
            generatedPassword.value = result.data.password;
        }
        const response = await getPasswordValidateRulesCommon(state.userRequestBody.user_type_id);
        passwordRules.base = response.data.base;
        passwordRules.optional = response.data.optional;
        isContentReady.value = true;
    } else {
        router.push('/');
        dxUiAlert({
            showTitle: false,
            showCloseButton: true,
            message: `<div class="text-center">${resultRestoreLink?.errors[0].message}<br>Воспользуйтесь кнопкой "Восстановить доступ"</div>`,
        });
    }
});

onUnmounted(() => {
    isContentReady.value = false;
});

const changePassword = async () => {
    isContentReady.value = false;
    isFirstTry.value = false;
    const result = await putPasswordRestoreLinkCommon(url, passwords.new.password);
    if (result && result.success) {
        router.push('/infoenergo/login');
        dxUiAlert({
            showTitle: false,
            showCloseButton: true,
            message: '<div class="text-center">Пароль успешно изменен!</div>',
        });
    } else {
        labelPassword.value = 'Пароль не соответствует следующим требованиям:';
        passwordRules.base = result?.errors.map(error => error.message);
        passwordRules.optional = [];
        passwords.new.valid = false;
        isContentReady.value = true;
    }
};
</script>

<style lang="scss" scoped>
.form-card {
    flex-shrink: 0;

    margin: auto;

    width: 400px;
    height: auto;
    min-height: 770px;
    padding: 32px;
    gap: 1.5rem;

    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 10px;
    box-shadow: 5px 5px 10px 5px #00000033;
    backdrop-filter: blur(10px);
}
</style>
